import { SB, WithMeta } from '@play-co/replicant';
import type { Replicant } from '@play-co/replicant';
import { petSchema } from './pet/pet.schema';
import { conversationSchema } from './conversation/conversation.schema';
import { inventorySchema } from './inventory/inventory.schema';
import { tutorialSchema } from './tutorial/tutorial.schema';
import { careSchema } from './care/care.schema';
import { profileState } from './profile/profile.state';
import { settings } from './state/settings';
import ab from './ruleset/ab';
import adminSchema from './admin/admin.schems';
import { socialSchema } from './social/social.schema';
import giftsSchema from './gifts/gifts.schema';
import { minigameSchema } from './minigame/minigame.schema';
import { match3Schema } from './minigame/match3/match3.schema';
import { levelSchema } from './level/level.schema';
import { encyclopediaSchema } from './encyclopedia/encyclopedia.schema';
import { visitSchema } from './visit/visit.schema';
import fateSchema from './fate/fate.schema';
import * as iapSchema from './iap/iap.schema';
import duoSchema from './duo/duo.schema';
import historySchema from './history/history.schema';
import * as nativeSchema from './native/native.schema';
import decorationSchema from './decoration/decoration.state';
import { airplaneSchema } from './minigame/airplane/airplane.schema';
import promoSchema from './promo/promo.schema';
import { competitiveSchema } from './competitive/competitive.schema';
import { accessorySchema } from './accessory/accessory.schema';
import { slotSchema } from './slots/slots.schema';
import { match2Schema } from './match2/match2.schema';
import { progressionSchema } from './progression/progression.schema';
import bumbumSchema from './minigame/bumbum/bumbum.schema';
import { chatbotSchema } from './chatbot/chatbot.schema';
import frenzySchema from './frenzy/frenzy.schema';
import { Immutable } from './utils/types';
import { costumeSchema } from './costume/costume.schema';
import { gachaSchema } from './gacha/gacha.schema';
import { analyticsSchema } from './analytics/analytics.schema';
import { m2migrationSchema } from './m2migration/migration.schema';
import { teamStateSchema } from './team/team.schema';
import { marketingSchema } from './marketing/marketing.schema';
import friendsSchema from './friends/friends.schema';
import { streakBattleRoyaleSchema } from './streakbattleroyale/streakbattleroyale.schema';
import adsSchema from './ads/ads.schema';
import statsSchema from './stats/stats.schema';
import { cardsSchema } from './cards/cards.schema';
import { superLollipopSchema } from './superLollipop/superLollipop.schema';
import { entrySchema } from './entry/entry.schema';
import { puzzleWinCountSchema } from './puzzleWinCount/puzzleWinCount.schema';
import { weeklyLeaderboardSchema } from './weeklyLeaderboard/weeklyLeaderboard.schema';
import { treasureExpressSchema } from './treasureExpress/treasureExpress.schema';
import { piggyBankSchema } from './piggyBank/piggyBank.schema';
import { championsLeagueSchema } from './championsLeague/championsLeague.schema';
import { rescueLevelSchema } from './rescueLevel/rescueLevel.schema';
import { resurrectionSchema } from './resurrection/resurrection.schema';
import { dailyFortuneShareSchema } from './dailyFortuneShare/dailyFortuneShare.schema';
import { buddiesSchema } from './buddies/buddies.schema';
import { jankenSchema } from './janken/janken.schema';
import { crateWaitingSchema } from './crateWaiting/crateWaiting.schema';
import { dreamDriftSchema } from './dreamDrift/dreamDrift.schema';
import { deprecatedStateSchema } from './deprecatedState';

export const stateSchema = SB.object({
  ...petSchema,
  ...conversationSchema,
  ...inventorySchema,
  ...tutorialSchema,
  ...careSchema,
  ...profileState,
  ...adminSchema,
  ...socialSchema,
  ...giftsSchema,
  ...minigameSchema,
  ...match3Schema(),
  ...levelSchema,
  ...encyclopediaSchema,
  ...visitSchema,
  ...fateSchema,
  ...iapSchema,
  ...duoSchema,
  ...historySchema,
  ...nativeSchema,
  ...decorationSchema,
  ...airplaneSchema,
  ...promoSchema,
  ...competitiveSchema,
  ...accessorySchema,
  ...slotSchema,
  ...match2Schema,
  ...progressionSchema,
  ...bumbumSchema,
  ...chatbotSchema,
  ...frenzySchema,
  ...costumeSchema,
  ...gachaSchema,
  ...analyticsSchema,
  ...m2migrationSchema,
  ...teamStateSchema,
  ...marketingSchema,
  ...friendsSchema,
  ...streakBattleRoyaleSchema,
  ...adsSchema,
  ...statsSchema,
  ...cardsSchema,
  ...superLollipopSchema,
  ...entrySchema,
  ...puzzleWinCountSchema,
  ...weeklyLeaderboardSchema,
  ...treasureExpressSchema,
  ...piggyBankSchema,
  ...championsLeagueSchema,
  ...rescueLevelSchema,
  ...resurrectionSchema,
  ...dailyFortuneShareSchema,
  ...buddiesSchema,
  ...jankenSchema,
  ...crateWaitingSchema,
  ...dreamDriftSchema,

  timezoneOffsetMs: SB.number(),

  abTests: SB.map(SB.string()),

  // Locale, sfx turn on, music turn on
  settings,

  // Map of values migrated over from platform storage
  // https://blackstormlabs.atlassian.net/browse/THUG-680
  platformStorage: SB.map(SB.unknown()).optional(),

  // Timestamp of user signalling intent to delete their account,
  // used in a two-step process to minimise unintended deletions
  deleteAccountIntent: SB.number().optional(),

  // Last offline update time
  offlineUpdateAt: SB.number(),

  // Last session time.
  lastSessionAt: SB.number(),

  // Previous session time.
  previousSessionAt: SB.number(),

  // Last reactivation time.
  lastReactivationAt: SB.number(),

  /**
   * @deprecated Replaced by the replicant-side `receivedMessagesSinceLastEntry`
   * property for analytics.
   * TODO: remove from state schema and delete in migration in future release.
   */
  chatbotMessagesSinceLastSession: SB.number(),

  /**
   * @deprecated Removed from `EntryFinal` analytics. Strings of chatbot
   * subfeatures, in order sent since the last entry.
   * TODO: remove from state schema and delete in migration in future release.
   */
  messageBreadcrumbs: SB.array(SB.string()),

  /**
   * @deprecated Removed from `EntryFinal` analytics. Count of messages since
   * last entry.
   * TODO: remove from state schema and delete in migration in future release.
   */
  messageBreadcrumbsCount: SB.number(),

  // Is decoration progression migration was done
  progressionMigration: SB.boolean().default(false),

  // Is decoration progression slots minigame migration was done
  slotsMinigameMigration: SB.boolean().default(false),

  // Do we allow user use decoration editor switch
  allowDecorationEditor: SB.boolean().default(false),

  // Have we been remediated for the 2306_10_mochi Frenzy leaderboard incident
  // (https://playco.atlassian.net/browse/LPG-2273)
  frenzyLeaderboardRemediation_2306_10_mochi: SB.boolean().default(false),

  // Has the user seen the new user experience for the first time login flow
  firstMiniAppSwitchAt: SB.number().optional(),

  // Has the user been redirected from mini app to liff because of control bucket
  firstLiffAppSwitchAt: SB.number().optional(),

  // Current active rich menu id for the user
  richMenuId: SB.string().optional(),

  // Fields belonging to removed features, awaiting their cleanup migration.
  ...deprecatedStateSchema,
});

export type UserSettings = SB.ExtractType<typeof settings>;
export type StateFromSchema = SB.ExtractType<typeof stateSchema>;

/**
 * Mutable state. Use for modifiers, messages, actions.
 */
export type MutableState = WithMeta<
  StateFromSchema,
  { abTests: typeof ab.dynamicAB }
>;

/**
 * Immutable state. Use in getters and outside replicant.
 */
export type State = Immutable<MutableState>;

export type AnyPayload = { [key: string]: any };

// Export the type of the replicant state API
export type ReplicantFromState<
  TPartialReplicant extends Omit<Partial<Replicant<{}>>, 'state'> = {},
> = Replicant<
  TPartialReplicant & {
    state: State;
  }
>;

/**
 * Creates a default state with replicant system state fields filled in. The result is mutable, for convenience.
 *
 * @param metaFields Optional overrides for the default values for the replicant state fields.
 */
export function getDefaultState(
  metaFields?: Partial<WithMeta<unknown>>,
): MutableState {
  return {
    ...stateSchema.getDefault(),

    // WithMeta fields.
    id: '',
    createdAt: 0,
    updatedAt: 0,
    ruleset: { abTests: {} },
    userSharedStates: {},
    platformStorage: {},

    ...metaFields,
  } as MutableState;
}
